import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from '@mulesoft/anypoint-components/lib/Select';
import Radio from '@mulesoft/anypoint-components/lib/Radio';
import RadioGroup from '@mulesoft/anypoint-components/lib/RadioGroup';
import { RUN_TIME_OPTIONS } from '../constants';
import { generateRunFrequencyOptions } from '../utils';
import styles from '../ScannerConfigurationForm.css';

/**
 * ScheduledRunSection Component
 *
 * Renders the scheduled run configuration section with:
 * - Run frequency selector
 * - Run time selector
 * - Sync review options
 */
const ScheduledRunSection = ({
  runFrequency,
  runTime,
  syncReview,
  onChange
}) => {
  const runFrequencyOptions = useMemo(
    () => generateRunFrequencyOptions(runFrequency),
    [runFrequency]
  );

  return (
    <section className={styles.section}>
      <div className={styles['section-title']}>Scheduled run configuration</div>

      <div className={styles.field}>
        <label htmlFor="run-frequency" className={styles.label}>
          Run Schedule
        </label>
        <div className={styles['field-content']}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              gap: '12px',
              width: '100%'
            }}
          >
            <Select
              id="run-frequency"
              name="run-frequency"
              testId="run-frequency"
              value={runFrequency}
              options={runFrequencyOptions}
              onChange={(event) =>
                onChange('runFrequency', event.itemsSelected?.value || '')
              }
            />
            <span className={styles['schedule-separator']}>at</span>
            <Select
              id="run-time"
              name="run-time"
              testId="run-time"
              value={runTime}
              options={RUN_TIME_OPTIONS}
              onChange={(event) =>
                onChange('runTime', event.itemsSelected?.value || '')
              }
            />
          </div>
        </div>
      </div>

      {/* Sync Review Field */}
      <div className={styles.field}>
        <label htmlFor="sync-review" className={styles.label}>
          Sync review
        </label>
        <div className={styles['field-content']}>
          <RadioGroup
            id="sync-review"
            name="syncReview"
            testId="sync-review"
            value={syncReview}
            orientation="horizontal"
            onChange={(event) => onChange('syncReview', event.value)}
          >
            <Radio
              testId="sync-review-auto-resolve"
              label="Auto-resolve"
              value="auto-resolve"
              style={{ marginRight: '110px' }}
            />
            <Radio
              testId="sync-review-ask-for-review"
              label="Ask for review"
              value="ask-for-review"
              disabled
            />
          </RadioGroup>
        </div>
      </div>
    </section>
  );
};

ScheduledRunSection.propTypes = {
  /** Current run frequency value */
  runFrequency: PropTypes.string.isRequired,
  /** Current run time value */
  runTime: PropTypes.string.isRequired,
  /** Current sync review value */
  syncReview: PropTypes.string.isRequired,
  /** Handler for field changes */
  onChange: PropTypes.func.isRequired
};

export default ScheduledRunSection;
